import { createClient } from "@supabase/supabase-js";

import { bootMark } from "@/src/lib/boot-mark";
import { logger } from "@/src/lib/logging/logger";
import { Database } from "@/src/lib/types/database.types";

bootMark("module:supabase-evaluated");

const SUPABASE_URL = process.env.SUPABASE_URL!;
const SUPABASE_ANON_KEY = process.env.SUPABASE_ANON_KEY!;
const SUPABASE_SERVICE_ROLE_KEY = process.env.SUPABASE_SERVICE_ROLE_KEY;
const SUPABASE_READONLY_KEY = process.env.SUPABASE_READONLY_KEY;
const SUPABASE_USERNAME = process.env.SUPABASE_USERNAME;
const SUPABASE_PASSWORD = process.env.SUPABASE_PASSWORD;

let serviceRoleClient: ReturnType<typeof createClient<Database>> | null = null;
let readonlyClient: ReturnType<typeof createClient<Database>> | null = null;
let authenticatedClient: ReturnType<typeof createClient<Database>> | null = null;
let sessionExpiresAt: number | null = null;

export const getSupabaseClient = async (): Promise<ReturnType<typeof createClient<Database>>> => {
  // For user authentication, use anon key and set the session
  if (SUPABASE_USERNAME && SUPABASE_PASSWORD) {
    // Check if we have a cached client and if the session is still valid
    if (authenticatedClient && sessionExpiresAt && Date.now() < sessionExpiresAt - 60000) {
      // 1 minute buffer
      return authenticatedClient;
    }

    const client = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY);

    try {
      const { data, error } = await client.auth.signInWithPassword({
        email: SUPABASE_USERNAME,
        password: SUPABASE_PASSWORD,
      });

      if (error) {
        logger.error("Supabase authentication failed", {
          fileName: "supabase.ts",
          error: error.message,
          code: error.status,
        });
        throw new Error(`Failed to sign in Supabase user: ${error.message}`);
      }

      if (!data.session) {
        logger.error("No session returned from Supabase authentication", {
          fileName: "supabase.ts",
        });
        throw new Error("No session returned from Supabase authentication");
      }

      logger.info("Successfully authenticated with Supabase", {
        fileName: "supabase.ts",
        userId: data.user?.id,
        sessionExpiresAt: data.session.expires_at,
      });

      // Set the session on the client
      client.auth.setSession(data.session);

      // Cache the client and session expiry
      authenticatedClient = client;

      sessionExpiresAt = (data.session.expires_at || 0) * 1000; // Convert to milliseconds

      return client;
    } catch (error) {
      logger.error("Error during Supabase authentication", {
        fileName: "supabase.ts",
        error: error,
      });
      throw error;
    }
  }

  if (SUPABASE_SERVICE_ROLE_KEY) {
    if (!serviceRoleClient) {
      serviceRoleClient = createClient<Database>(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY);
    }

    return serviceRoleClient;
  }

  if (SUPABASE_READONLY_KEY) {
    if (process.env.NODE_ENV === "production") {
      throw new Error("SUPABASE_READONLY_KEY auth is only available outside production");
    }

    if (!SUPABASE_ANON_KEY) {
      throw new Error("SUPABASE_ANON_KEY is required when SUPABASE_READONLY_KEY is set");
    }

    if (!readonlyClient) {
      readonlyClient = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
        global: { headers: { Authorization: `Bearer ${SUPABASE_READONLY_KEY}` } },
      });
    }

    return readonlyClient;
  }

  throw new Error("Missing Supabase credentials: set username/password, service role key, or readonly key");
};
